<template>
  <ThePage v-if="product" :title="product.name">
    <template #pageTitle>
      {{ product.name }}
    </template>
    <template #pageContent>
      <div class="body-1 flex gap-12">
        <FormattedMessage :definition="translations.productId">
          <template #id>
            <span class="body-1-bold">
              {{ product.publicId }}
            </span>
          </template>
        </FormattedMessage>

        <FormattedMessage :definition="translations.backmarketId">
          <template #id>
            <span class="body-1-bold">
              {{ product.id }}
            </span>
          </template>
        </FormattedMessage>

        <FormattedMessage
          v-if="product.manufacturerVersion"
          :definition="translations.manufacturerVersion"
        >
          <template #manufacturerVersion>
            <span class="body-1-bold">
              {{ product.manufacturerVersion }}
            </span>
          </template>
        </FormattedMessage>

        <FormattedMessage v-if="product.mpn" :definition="translations.mpn">
          <template #mpn>
            <span class="body-1-bold">
              {{ product.mpn }}
            </span>
          </template>
        </FormattedMessage>
      </div>

      <div class="border-static-default-low grid-standard mt-12">
        <RevForm
          class="md:col-span-3 lg:col-span-8"
          :initial-values
          name="listing-create"
          :validate="validateForm"
        >
          <template #default="{ values, errors, isValid }">
            <div class="space-y-12">
              <h2 class="heading-2">
                {{ i18n(translations.generalInfos) }}
              </h2>

              <div class="flex justify-between">
                <span class="body-1-bold">
                  {{ i18n(translations.aestheticCondition) }}
                </span>

                <RevLink
                  v-if="hasSupportFeatureEnabled"
                  target="_blank"
                  :to="SUPPORT_CENTER_LINK"
                >
                  {{ i18n(translations.learnMore) }}
                </RevLink>
              </div>

              <div class="flex gap-8">
                <RevPicker
                  v-for="aestheticGrade in metadata?.aestheticGrades"
                  :key="aestheticGrade.value"
                  :label="aestheticGrade.label"
                  :model-value="values.aestheticGrade === aestheticGrade.value"
                  @update:model-value="
                    onChooseAestheticGrade(aestheticGrade.value)
                  "
                />
              </div>

              <div class="flex gap-8">
                <RevInputText
                  id="sku"
                  v-model="values.sku"
                  class="flex-1"
                  :error="errors.sku"
                  :label="i18n(translations.sku)"
                />

                <RevInputText
                  id="stock"
                  v-model="values.stock"
                  class="flex-1"
                  :error="errors.stock"
                  :label="i18n(translations.stock)"
                  :min="MIN_STOCK"
                  type="number"
                />
              </div>

              <RevToggle
                v-if="shouldDisplayNewBatteryToggle"
                v-model="values.shouldHaveNewBattery"
                :disabled="shouldDisableNewBatteryToggle(values.aestheticGrade)"
                :label="i18n(translations.newBatteryToggleTitle)"
                position="left"
                @update:model-value="
                  (shouldHaveNewBattery) =>
                    onNewBatteryToggle(shouldHaveNewBattery)
                "
              >
                <FormattedMessage
                  :definition="translations.newBatteryToggleContent"
                >
                  <template #link>
                    <RevLink target="_blank" :to="NEW_BATTERY_OFFER_LINK">
                      {{ i18n(translations.newBatteryToggleContentLink) }}
                    </RevLink>
                  </template>
                </FormattedMessage>
              </RevToggle>
            </div>

            <div class="mt-32 space-y-12">
              <h2 class="heading-2">
                {{ i18n(translations.price) }}
              </h2>

              <div
                v-if="errors.markets"
                class="text-static-danger-hi body-1-bold mt-4"
              >
                {{ errors.markets }}
              </div>

              <div class="relative">
                <div class="absolute right-0 top-0">
                  <RevButton
                    size="small"
                    variant="primary"
                    @click="setBackboxForAllMarket(values)"
                  >
                    {{ i18n(translations.getAllBackbox) }}
                  </RevButton>
                </div>

                <RevTable
                  :collection="marketList"
                  :columns="tableColumns"
                  striped-rows
                  transparent-header
                >
                  <template #body-market="{ item }">
                    <RevButtonBase
                      class="flex"
                      target="_blank"
                      :to="item.productPageLink.href"
                    >
                      <Flag class="mr-12" :country-code="item.market" />

                      {{ item.market }}
                    </RevButtonBase>
                  </template>

                  <template #body-backboxPrice="{ item }">
                    <span v-if="!isFetchingBackboxInfo">
                      {{
                        item.backboxPrice
                          ? i18n.price({
                              amount: item.backboxPrice,
                              currency: item.currency,
                            })
                          : '-'
                      }}
                    </span>
                    <RevSpinner v-if="isFetchingBackboxInfo" />
                  </template>

                  <template #body-currentBMPrice="{ item }">
                    <template v-if="!isFetchingBackboxInfo">
                      <span v-if="item.winnerPrice">
                        {{
                          i18n.price({
                            amount: item.winnerPrice,
                            currency: item.currency,
                          })
                        }}
                      </span>

                      <RevTooltip v-else position="top">
                        <template #trigger="{ hide, show }">
                          <div class="flex items-center">
                            <RevTag
                              :label="i18n(translations.noCompetitionTitle)"
                              variant="success"
                            />
                            <RevButtonIcon
                              :icon="IconInfo"
                              type="button"
                              variant="secondary"
                              @blur="hide"
                              @focus="show"
                              @mouseenter="show"
                              @mouseleave="hide"
                            />
                          </div>
                        </template>

                        {{ i18n(translations.noCompetitionInfo) }}
                      </RevTooltip>
                    </template>

                    <RevSpinner v-if="isFetchingBackboxInfo" />
                  </template>

                  <template #body-myPrice="{ item }">
                    <RevInputText
                      :id="`price-${item.market}`"
                      v-model="values.markets[item.market]"
                      :label="
                        i18n(translations.priceInput, {
                          currency: i18n.currencySign(item.currency),
                        })
                      "
                      :min="1"
                      type="number"
                    />
                  </template>

                  <template #body-action="{ item }">
                    <RevButton
                      v-if="item.backboxPrice"
                      size="small"
                      variant="secondary"
                      @click="handleGetBackbox(values, item)"
                    >
                      {{ i18n(translations.getBackbox) }}
                    </RevButton>
                  </template>
                </RevTable>
              </div>
            </div>

            <div class="mt-32 space-y-12">
              <h2 class="heading-2">
                {{ i18n(translations.warranty) }}
              </h2>

              <p class="body-1">
                {{ i18n(translations.warrantyDescription) }}
              </p>

              <div class="flex gap-12">
                <RevButton
                  data-qa="save-listing"
                  type="submit"
                  variant="primary"
                  @click="
                    submitListing({
                      values,
                      isValid,
                      shouldRedirectToNewListing: false,
                    })
                  "
                >
                  {{ i18n(translations.saveMyListing) }}
                </RevButton>

                <RevButton
                  data-qa="save-listing-and-continue"
                  type="submit"
                  variant="secondary"
                  @click="
                    submitListing({
                      values,
                      isValid,
                      shouldRedirectToNewListing: true,
                    })
                  "
                >
                  {{ i18n(translations.saveMyListingAndCreateNewOne) }}
                </RevButton>
              </div>
            </div>
          </template>
        </RevForm>

        <div class="md:col-span-1 lg:col-span-4">
          <RevCarousel
            :alternative-button-label="
              i18n(translations.alternativeCurrentText)
            "
            :alternative-controller="
              i18n(translations.alternativeControllerText)
            "
            :alternative-current="i18n(translations.alternativeCurrentText)"
            :alternative-next="i18n(translations.alternativeCurrentText)"
            :alternative-previous="i18n(translations.alternativeCurrentText)"
            :controller-images="thumbnails"
            controller-position="right"
            controller-variant="thumbnails"
            current-index-id="product-thumbnail-carousel"
            :display-navigation="false"
          >
            <RevIllustration
              v-for="image in thumbnails"
              :key="image.alt"
              :alt="image.alt"
              :height="228"
              :src="image.src"
              :width="198"
            />
          </RevCarousel>
        </div>
      </div>
    </template>
  </ThePage>
  <RevLoadingScreen v-else :text="i18n(translations.loadingText)" />
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError, merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useHttpFetch as useHttpFetchV2 } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCarousel } from '@ds/components/Carousel'
import {
  FORM_VALID,
  RevForm,
  makeValidate,
  required,
  setFormValues,
} from '@ds/components/Form'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPicker } from '@ds/components/Picker'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { RevToggle } from '@ds/components/Toggle'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import type { ListingCreateBody } from '~/api/listings/types'
import { getExternalLinkList } from '~/constants/externalLinks'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import {
  MIN_STOCK,
  SPECIAL_OFFER_TYPES,
} from '~/scopes/listings/Listings.constants'

import { useListingPagePreference } from '../listingsV2/composables/useListingPagePreference'

import {
  getActiveListingsRoute,
  getListingCreateRoute,
  shouldDisableNewBatteryToggle,
} from './Create.helpers'
import translations from './Create.translations'

const $httpFetch = useHttpFetchV2()

const { isFeatureEnabled } = useBusinessFeaturesStore()
const marketplace = useMarketplace()
const { isNewBatteryListingsEnabled, onboardingCompleted } =
  useSellerInfoStore()
const route = useRoute()
const i18n = useI18n()
const tracking = useTracking()
const { openErrorToast } = useTheToast()
const hasSupportFeatureEnabled = isFeatureEnabled('supportEnabled')
const { SUPPORT_CENTER_LINK, NEW_BATTERY_OFFER_LINK } = getExternalLinkList(
  i18n.currentLocale,
)

const { isListingsPageV2Default } = useListingPagePreference()
const tableColumns = [
  {
    key: 'market',
    label: i18n(translations.market),
  },
  {
    key: 'backboxPrice',
    label: i18n(translations.backboxPrice),
  },
  {
    key: 'currentBMPrice',
    label: i18n(translations.currentBMPrice),
  },
  {
    key: 'myPrice',
    label: i18n(translations.myPrice),
  },
  {
    key: 'action',
    label: '',
  },
]

const { data: metadata } = await useHttpFetch(merchantsAPI.getListingMetadata, {
  pathParams: {
    productId: route.params.productId,
  },
})
const currentAestheticGrade = ref(metadata.value?.aestheticGrades[0].value)
const currentShouldHaveNewBattery = ref(false)

const specialOfferType = computed(() =>
  currentShouldHaveNewBattery.value ? 7 : 0,
)

const { data: backboxInfo, pending: isFetchingBackboxInfo } = useHttpFetch(
  merchantsAPI.getBackboxInfo,
  {
    queryParams: {
      productId: metadata?.value?.product.publicId,
      aestheticGrade: currentAestheticGrade,
      // special_offer_type at 0: see: https://backmarket.atlassian.net/browse/GM-3771?focusedCommentId=86746
      specialOfferType,
    },
  },
)

const initialValues = {
  aestheticGrade: currentAestheticGrade.value,
  markets: Object.fromEntries(
    metadata.value?.product.availableMarkets.map(({ market }) => [
      market,
      '',
    ]) ?? [],
  ),
  shouldHaveNewBattery: false,
  sku: '',
  stock: '',
}

const isRequired = required(i18n(translations.required))

const validateForm = makeValidate<typeof initialValues>({
  aestheticGrade: [isRequired],
  sku: [isRequired],
  stock: [isRequired],
  markets: [
    (value) => {
      return Object.values(value as Record<string, string>).some(
        (price) => price !== '',
      )
        ? FORM_VALID
        : i18n(translations.priceError)
    },
  ],
})

const shouldDisplayNewBatteryToggle = computed(() => {
  return (
    isNewBatteryListingsEnabled && metadata.value?.product.isNewBatteryEnabled
  )
})

const product = computed(() => metadata.value?.product)

const marketList = computed(() => {
  return (
    metadata.value?.product.availableMarkets.map(
      ({ market, productPageLink }) => {
        const relatedMarket = backboxInfo.value?.find((backbox) => {
          return backbox.country === market
        })

        const relatedMarketCurrency =
          marketplace.allMarkets[market]?.defaultCurrency

        return {
          action: '',
          backboxPrice: relatedMarket?.backboxPrice || '',
          currency: relatedMarketCurrency,
          currentBMPrice: '312',
          market,
          productPageLink,
          winnerPrice: relatedMarket?.winnerPrice || '',
        }
      },
    ) || []
  )
})

const thumbnails = computed(
  () =>
    metadata.value?.product.thumbnails.map((thumbnail) => ({
      alt: '',
      src: thumbnail,
    })) || [],
)

function onNewBatteryToggle(shouldHaveNewBattery: boolean) {
  currentShouldHaveNewBattery.value = shouldHaveNewBattery
  setFormValues('listing-create', {
    shouldHaveNewBattery,
  })
}

function onChooseAestheticGrade(aestheticGradeValue: number) {
  currentAestheticGrade.value = aestheticGradeValue
  setFormValues('listing-create', {
    aestheticGrade: aestheticGradeValue,
  })
}

function handleGetBackbox(
  values: { markets: Record<string, string> },
  item: {
    market: string
    backboxPrice: string
    winnerPrice: string
    currency: string
  },
) {
  // eslint-disable-next-line no-param-reassign
  values.markets[item.market] = item.backboxPrice

  tracking.trackClick({
    page_type: 'listing_create',
    zone: 'listing_price_creation',
    name: 'win-backbox',
    listing_title: product.value?.name ?? '',
    product_public_id: product.value?.publicId ?? '',
    listing_backbox_grade: currentAestheticGrade.value ?? '',
    listing_markets: [
      {
        market: item.market,
        currency: item.currency,
        backbox_price: item.backboxPrice,
        current_backbox_price: item.winnerPrice,
        price_to_win_backbox: item.backboxPrice,
      },
    ],
  })
}

function setBackboxForAllMarket(values: { markets: Record<string, string> }) {
  marketList.value.forEach((market) => {
    // eslint-disable-next-line no-param-reassign
    values.markets[market.market] = market.backboxPrice
  })

  tracking.trackClick({
    page_type: 'listing_create',
    zone: 'listing_price_creation',
    name: 'win-all-backbox',
    listing_title: product.value?.name ?? '',
    product_public_id: product.value?.publicId ?? '',
    listing_backbox_grade: currentAestheticGrade.value ?? '',
    listing_markets: marketList.value
      .filter((marketItem) => marketItem.backboxPrice)
      .map((marketItem) => ({
        market: marketItem.market,
        currency: marketItem.currency,
        backbox_price: marketItem.backboxPrice,
        current_backbox_price: marketItem.winnerPrice,
        price_to_win_backbox: marketItem.backboxPrice,
      })),
  })
}

async function submitListing({
  values,
  isValid,
  shouldRedirectToNewListing,
}: {
  values: typeof initialValues
  isValid: boolean
  shouldRedirectToNewListing: boolean
}) {
  if (isValid) {
    try {
      const { aestheticGrade, sku, stock, markets, shouldHaveNewBattery } =
        values
      const marketsWithPrice: Record<string, string>[] = []

      Object.entries(markets).forEach(([market, price]) => {
        if (price !== '') {
          marketsWithPrice.push({ market, price: String(price) })
        }
      })

      const body: ListingCreateBody = {
        aestheticGrade,
        sku,
        stock,
        markets: marketsWithPrice,
        productId: metadata.value?.product.publicId,
      }

      if (shouldHaveNewBattery) {
        body.specialOfferType = SPECIAL_OFFER_TYPES.NEW_BATTERY
      }

      await $httpFetch('/api/seller-experience/listings', {
        method: 'POST',
        body,
      })

      tracking.trackFormSubmit({
        product_listing_id: metadata.value?.product.id ?? '',
        sku_id: body.sku,
        page_type: 'listing_create_details',
      })

      navigateTo({
        name: shouldRedirectToNewListing
          ? getListingCreateRoute(onboardingCompleted)
          : getActiveListingsRoute(
              onboardingCompleted,
              isListingsPageV2Default.value,
            ),
      })
    } catch (error) {
      const { title: content } = HttpApiError.fromAnyError(error as Error)

      openErrorToast({
        content,
      })
    }
  } else {
    // the errors message are on top of the form. The scroll to is here to let the user
    // automatically see them
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>
